import styled, { keyframes } from "styled-components";
import { Tabs } from "antd";
import {
  brand,
  border,
  surface,
  text,
  status,
  typography,
  spacing,
  radius,
} from "../../theme/tokens";

/**
 * AI panel chrome.
 *
 * Visual language ("Option B — subtle AI surface treatment"):
 *   - 3px brand-blue gradient strip at the very top of the panel.
 *   - A sparkle glyph next to a small uppercase "AI" label in the header.
 *   - Brand-blue 2px underline indicator on the active tab (matches the
 *     existing Mail Intake `IntakeNavTabs` ink-bar treatment).
 *   - Strategic Overview card (Phase 4) gets a 2px brand-blue left border;
 *     not introduced here, but the rest of the chrome is calm so that
 *     surface reads as the visually distinct synthesis card.
 *
 * The panel sits inside the existing `ChatPaneWrapper` slot in
 * `MatterDetails` (500px fixed). A future phase may make it resizable;
 * for v1 we deliberately match the current ChatPane footprint so layout
 * code in `MatterDetails`/`TabView` doesn't need to change beyond the
 * URL-state rename.
 */

export const AIPaneContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  width: 100%;
  background: ${surface.surface};
  font-family: ${typography.fontFamily};
  color: ${text.primary};
  overflow: hidden;
  box-sizing: border-box;
  position: relative;
`;

/**
 * The "AI surface" gradient strip. Subtle by design — strong enough to
 * read as differentiation from the regular DMS chrome, faint enough that
 * it doesn't compete with the active-tab indicator.
 */
export const AIGradientStrip = styled.div`
  flex-shrink: 0;
  height: 3px;
  background: linear-gradient(
    90deg,
    ${brand.primary} 0%,
    rgba(12, 83, 187, 0.6) 35%,
    rgba(12, 83, 187, 0) 100%
  );
`;

export const AIPaneHeader = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: ${({ $collapsed }) =>
    $collapsed ? "center" : "space-between"};
  padding: ${({ $collapsed }) =>
    $collapsed ? `${spacing[2]} 0` : `${spacing[3]} ${spacing[4]}`};
  border-bottom: 1px solid ${border.light};
`;

export const AIPaneHeaderLabelGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
`;

export const AIPaneHeaderLabel = styled.span`
  font-size: ${typography.size.sm};
  font-weight: ${typography.weight.bold};
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: ${brand.primary};
`;

export const AIPaneCloseButton = styled.button`
  background: none;
  border: none;
  padding: ${spacing[1]};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${text.tertiary};
  border-radius: ${radius.sm};
  transition: background-color 0.15s ease, color 0.15s ease;

  &:hover {
    background: ${surface.hover};
    color: ${text.primary};
  }

  &:focus-visible {
    outline: 2px solid ${brand.primary};
    outline-offset: 1px;
  }
`;

export const AIPaneTabs = styled(Tabs)`
  /*
   * Chat-only modes (VA CAVC) hide the tab strip instead of swapping to
   * a different wrapper component. The panel's mode can flip after
   * mount (matter details load async; the mode is derived from
   * case_type once the payload lands) and replacing the Tabs node with
   * a different parent unmounts the chat subtree — losing any
   * conversation started during the loading window (Bugbot 265/R2).
   * Keeping the SAME Tabs component and hiding its nav preserves the
   * chat pane's element identity across the flip.
   */
  ${(p) => (p.$hideTabBar ? "&& .ant-tabs-nav { display: none; }" : "")}

  /*
   * Collapsed inbox rail: the whole tab surface is hidden (not
   * unmounted) so the per-tab fetch state survives a collapse/expand
   * round trip and the pane comes back exactly as it was left.
   */
  ${(p) => (p.$collapsed ? "&&.ant-tabs { display: none; }" : "")}

  &.ant-tabs {
    /*
     * AIPaneTabs lives inside AIPaneContainer (a flex column with
     * height: 100%). The gradient strip + header above are
     * flex-shrink: 0, so this Tabs node owns the rest of the panel
     * height. We need the full flex chain — Tabs → content holder →
     * content → active tab pane → AIPaneTabContent → AIPaneScrollBody
     * — to all be flex columns with min-height: 0 so vertical overflow
     * activates inside AIPaneScrollBody. Without this chain, long
     * briefings/analyses get clipped by the container's overflow:
     * hidden instead of scrolling.
     */
    flex: 1 1 0;
    height: 100%;
    max-height: 100%;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    padding: 0 ${spacing[4]};
    border-bottom: 1px solid ${border.light};
    background: ${surface.surface};

    .ant-tabs-nav { margin: 0; flex-shrink: 0; }
    .ant-tabs-nav-list { gap: 0; }

    .ant-tabs-content-holder {
      flex: 1 1 0;
      height: 100%;
      max-height: 100%;
      min-height: 0;
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }

    .ant-tabs-content {
      flex: 1 1 0;
      height: 100%;
      max-height: 100%;
      min-height: 0;
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }

    .ant-tabs-tabpane {
      min-height: 0;
      height: 100%;
      max-height: 100%;
      overflow: hidden;
    }

    .ant-tabs-tabpane-active {
      flex: 1 1 0;
      min-height: 0;
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }

    .ant-tabs-tab {
      padding: ${spacing[3]} ${spacing[4]};
      margin: 0;
      .ant-tabs-tab-btn {
        font-family: ${typography.fontFamily};
        font-weight: ${typography.weight.medium};
        font-size: ${typography.size.md};
        color: ${text.tertiary};
      }
    }
    .ant-tabs-tab:focus,
    .ant-tabs-tab:focus-visible { outline: none !important; box-shadow: none !important; }
    .ant-tabs-tab div { outline: none !important; }
    .ant-tabs-tab-active .ant-tabs-tab-btn {
      font-weight: ${typography.weight.semibold};
      color: ${brand.primary} !important;
    }
    .ant-tabs-ink-bar {
      background: ${brand.primary};
      height: 2px;
    }
  }
`;

export const AIPaneTabContent = styled.div`
  flex: 1 1 0;
  height: 100%;
  max-height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: ${surface.surface};
  overflow: hidden;
`;


/*
 * NOTE: every scrollable container in this file carries
 * `className: "intentional-scroll"`. The app-level
 * `useRouteScrollCleanup` hook (see `src/utils/scrollCleanup.js`,
 * mounted from `src/AppRouter.js`) fires on every pathname change
 * — including matter-internal document switches like
 * `/matter/5/D10` → `/matter/5/D20` triggered by an evidence-link
 * click in The Brief. Its DOM sweep strips the inline
 * `overflow` style from any `div[style*="overflow"]` that does
 * NOT carry the `intentional-scroll` class, which used to silently
 * freeze the AI panel's body the moment the user clicked any
 * citation pill (the styled-component's CSS rule is still there,
 * but the inline-style cleanup wins). The class on these
 * containers makes the cleanup pass skip them so the AI panel
 * keeps its scrollbars across navigation.
 */
export const AIPaneScrollBody = styled.div.attrs({
  className: "intentional-scroll",
})`
  flex: 1 1 0;
  max-height: 100%;
  min-height: 0;
  overflow-y: auto;
  padding: ${spacing[5]} ${spacing[4]};
  font-size: ${typography.size.md};
  line-height: 1.7;
  color: ${text.primary};
`;

// ---------------------------------------------------------------------------
// Briefing tab
// ---------------------------------------------------------------------------

export const BriefingCategoryRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing[2]};
  margin-bottom: ${spacing[4]};
`;

export const BriefingTimestamp = styled.span`
  font-size: ${typography.size.sm};
  color: ${text.muted};
`;

export const BriefingMarkdownWrapper = styled.div`
  font-size: ${typography.size.md};
  line-height: 1.7;
  color: ${text.primary};

  h1, h2, h3, h4 {
    font-family: ${typography.fontFamily};
    color: ${text.primary};
    font-weight: ${typography.weight.semibold};
    margin: 20px 0 8px;
  }
  h1 { font-size: ${typography.size.lg}; }
  h2 { font-size: ${typography.size.lg}; }
  h3 { font-size: ${typography.size.md}; }
  h4 { font-size: ${typography.size.md}; }

  p {
    margin: 0 0 12px;
    color: ${text.primary};
    line-height: 1.7;
  }

  ul, ol {
    margin: 0 0 12px;
    padding-left: 22px;
  }
  li { margin-bottom: 4px; }

  a {
    color: ${brand.primary};
    text-decoration: none;
    &:hover { text-decoration: underline; }
  }

  hr {
    border: none;
    border-top: 1px solid ${border.light};
    margin: 20px 0;
  }

  strong {
    color: ${text.primary};
    font-weight: ${typography.weight.semibold};
  }
`;

export const CitationPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: min(100%, 360px);
  padding: 2px 8px;
  margin: 0 2px;
  font-size: ${typography.size.xs};
  font-weight: ${typography.weight.medium};
  color: ${brand.primary};
  background: ${status.processing.bg};
  border: 1px solid ${status.processing.border};
  border-radius: ${radius.pill};
  vertical-align: baseline;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};
  transition: background-color 0.15s ease;

  svg {
    flex: 0 0 auto;
  }

  span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &:hover {
    background: ${({ $clickable }) =>
      $clickable ? "rgba(12, 83, 187, 0.15)" : status.processing.bg};
  }
`;

export const BriefingDivider = styled.hr`
  border: none;
  border-top: 1px solid ${border.light};
  margin: ${spacing[5]} 0 ${spacing[4]};
`;

export const RunDeepAnalysisLink = styled.button`
  background: none;
  border: none;
  padding: ${spacing[2]} 0;
  font-family: ${typography.fontFamily};
  font-size: ${typography.size.md};
  font-weight: ${typography.weight.semibold};
  color: ${brand.primary};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: color 0.15s ease;

  &:hover { color: ${brand.primaryHover}; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &:focus-visible {
    outline: 2px solid ${brand.primary};
    outline-offset: 2px;
    border-radius: ${radius.sm};
  }
`;

// ---------------------------------------------------------------------------
// Analysis tab — empty state + running stub
// ---------------------------------------------------------------------------

export const AnalysisEmptyWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: ${spacing[8]} ${spacing[6]};
  gap: ${spacing[4]};
`;

export const AnalysisEmptyIconRing = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: ${brand.primaryLight};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${brand.primary};
  margin-bottom: ${spacing[2]};
`;

export const AnalysisEmptyTitle = styled.h3`
  margin: 0;
  font-size: ${typography.size.lg};
  font-weight: ${typography.weight.semibold};
  color: ${text.primary};
`;

export const AnalysisEmptyDescription = styled.p`
  margin: 0;
  max-width: 320px;
  font-size: ${typography.size.md};
  line-height: 1.6;
  color: ${text.secondary};
`;

export const AnalysisRunningWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[4]};
  padding: ${spacing[5]} ${spacing[4]};
`;

export const AnalysisErrorBanner = styled.div`
  padding: ${spacing[3]} ${spacing[4]};
  background: ${status.failed.bg};
  border: 1px solid ${status.failed.border};
  color: ${status.failed.fg};
  border-radius: ${radius.md};
  font-size: ${typography.size.sm};
`;

// ---------------------------------------------------------------------------
// Analysis tab — Phase 3 progress view
// ---------------------------------------------------------------------------
//
// Layout (top → bottom inside AnalysisProgressContainer):
//   [ status pill ]  [ elapsed time ]
//   "Analyzing 5 conditions"      <- section header
//   [ antd Progress bar — completed_conditions / total_conditions ]
//   [ ConditionList ]
//     [ ConditionRow ] PTSD                      [ status icon ]
//     [ ConditionRow ] Tinnitus                  [ status icon ]
//     ...
//   [ optional error banner ]
//   [ optional Try Again / View Report buttons (Phase 4 fills in the View
//     Report path) ]

// See `intentional-scroll` note on AIPaneScrollBody above.
export const AnalysisProgressContainer = styled.div.attrs({
  className: "intentional-scroll",
})`
  display: flex;
  flex-direction: column;
  gap: ${spacing[5]};
  padding: ${spacing[5]} ${spacing[4]};
  flex: 1 1 0;
  height: 100%;
  max-height: 100%;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
`;

export const AnalysisProgressHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing[3]};
`;

/**
 * Status pill background/foreground varies by job status:
 *   running                 → processing (blue)
 *   completed               → ready (green)
 *   completed_with_errors   → review (amber)
 *   failed | cancelled      → failed (red)
 *   anything else           → neutral (grey)
 */
const STATUS_PILL_TONES = {
  running: status.processing,
  completed: status.ready,
  completed_with_errors: status.review,
  failed: status.failed,
  cancelled: status.failed,
  // Phase 5 — Context Analysis pending state. We deliberately reuse
  // the neutral tone (rather than a 5th color) because pending is a
  // common "scaffold not yet computed" state for inbox docs and we
  // don't want it to read as a warning or error. The sparkle icon
  // adjacent to the pill carries the AI semantic.
  pending: status.neutral,
};

export const AnalysisStatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1.5]};
  padding: ${spacing[1]} ${spacing[2.5]};
  border-radius: ${radius.pill};
  font-size: ${typography.size.sm};
  font-weight: ${typography.weight.semibold};
  text-transform: capitalize;
  background: ${({ $status }) =>
    (STATUS_PILL_TONES[$status] || status.neutral).bg};
  color: ${({ $status }) =>
    (STATUS_PILL_TONES[$status] || status.neutral).fg};
  border: 1px solid
    ${({ $status }) =>
      (STATUS_PILL_TONES[$status] || status.neutral).border};
`;

export const AnalysisStatusPillDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
`;

export const AnalysisElapsedTime = styled.span`
  font-size: ${typography.size.sm};
  color: ${text.muted};
  font-variant-numeric: tabular-nums;
`;

export const AnalysisProgressSectionHeader = styled.h4`
  margin: 0;
  font-size: ${typography.size.md};
  font-weight: ${typography.weight.semibold};
  color: ${text.primary};
`;

export const AnalysisProgressBarWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[1]};

  /*
   * Tighten antd's Progress so it sits flush in the AI panel rail.
   * antd's default trail is too pale on white; nudge to brand light.
   */
  .ant-progress-outer { padding-inline-end: 0; }
  .ant-progress-bg { background: ${brand.primary}; }
  .ant-progress-inner { background: ${brand.primaryLight}; }
  .ant-progress-text {
    font-size: ${typography.size.sm};
    color: ${text.tertiary};
    font-variant-numeric: tabular-nums;
  }
`;

export const AnalysisProgressCounts = styled.span`
  font-size: ${typography.size.sm};
  color: ${text.muted};
  font-variant-numeric: tabular-nums;
`;

// The full progress pane owns scrolling. Let the checklist take its natural
// height so users can wheel anywhere in the pane and reach every condition.
export const ConditionList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  flex: 0 0 auto;
  min-height: 0;
  gap: ${spacing[1]};
  border: 1px solid ${border.light};
  border-radius: ${radius.md};
  overflow: visible;
`;

export const ConditionRow = styled.li`
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
  padding: ${spacing[2.5]} ${spacing[3]};
  border-bottom: 1px solid ${border.light};
  background: ${surface.surface};
  flex-shrink: 0;

  &:last-child { border-bottom: none; }
`;

export const ConditionRowName = styled.span`
  flex: 1;
  font-size: ${typography.size.md};
  color: ${text.primary};
  font-weight: ${typography.weight.medium};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/**
 * Short trailing facts on a checklist row (the VA status). The name
 * owns the row: this never grows, never shrinks the name to nothing,
 * and clamps itself if a status label runs long (Whitley 2026-08-23 —
 * unbounded meta text crushed every condition name to one letter).
 */
export const ConditionRowMeta = styled.span`
  font-size: ${typography.size.xs};
  color: ${text.muted};
  text-transform: capitalize;
  flex: 0 0 auto;
  max-width: 45%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/**
 * Tone container for the per-condition status icon. Keeps the row
 * height stable across all four states (icon vs spinner).
 */
const CONDITION_TONE = {
  pending: status.neutral,
  analyzing: status.processing,
  completed: status.ready,
  failed: status.failed,
};

export const ConditionStatusIconWrap = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ $status }) =>
    (CONDITION_TONE[$status] || status.neutral).bg};
  color: ${({ $status }) =>
    (CONDITION_TONE[$status] || status.neutral).fg};
  border: 1px solid
    ${({ $status }) =>
      (CONDITION_TONE[$status] || status.neutral).border};
`;

export const AnalysisActionRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  flex-wrap: wrap;
`;

export const AnalysisHelperText = styled.p`
  margin: 0;
  font-size: ${typography.size.sm};
  color: ${text.muted};
  line-height: 1.5;
`;

// ---------------------------------------------------------------------------
// Analysis tab — Phase 4 report view
// ---------------------------------------------------------------------------
//
// Layout (top → bottom inside AnalysisReportContainer):
//   [ ReportHeaderRow — finished-at, totals, status pill ]
//   [ StrategicOverviewCard — 2px brand-blue left border + pale tint ]
//   [ ConditionAnalysisCollapse — antd Collapse with one Panel per
//     condition; each panel header carries the chip strip + status icon,
//     each panel body holds the markdown analysis ]
//   [ ReportFooter — Export action ]
//
// Visual hierarchy: the Strategic Overview card is the loudest surface
// (brand-blue accent + slight tint), the per-condition cards are calm
// dividers so the user can skim conditions and dive into individual
// markdown bodies as needed.

// See `intentional-scroll` note on AIPaneScrollBody above. Currently
// not used by `AnalysisReport` (which renders an inline-styled scroll
// container with the same class), but exported for parity with the
// other AI-panel scroll containers and to future-proof against a
// re-extraction of this surface back into a styled component.
export const AnalysisReportContainer = styled.div.attrs({
  className: "intentional-scroll",
})`
  display: flex;
  flex-direction: column;
  gap: ${spacing[5]};
  padding: ${spacing[5]} ${spacing[4]} ${spacing[6]};
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`;

export const ReportHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing[3]};
  flex-wrap: wrap;
`;

export const ReportHeaderMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[1]};
`;

export const ReportHeaderTitle = styled.div`
  font-size: ${typography.size.md};
  font-weight: ${typography.weight.semibold};
  color: ${text.primary};
`;

export const ReportHeaderSubtitle = styled.div`
  font-size: ${typography.size.sm};
  color: ${text.muted};
`;

/**
 * Strategic Overview card — the synthesis pulled out of
 * `discovery_data.strategic_narrative`. Visually distinct from the
 * per-condition stack via:
 *   - a 3px brand-blue left rail (echoes the panel-header gradient strip)
 *   - a pale brand tint background (`brand.primaryLight`)
 *   - heavier vertical padding so it reads as a hero block
 *
 * The body uses the existing BriefingMarkdown component so citation
 * pills + GFM (tables / strikethrough / task lists) work the same as
 * the inbox briefing.
 */
/**
 * Combined-rating strip — the deterministic 38 CFR 4.25/4.26 number the
 * BE computes from codesheet determinations only (never the LLM).
 * Rounded value leads; the exact table math sits beneath it in small
 * muted text, mirroring the Salesforce matter header (Matthew,
 * 2026-08-25: "This block should have in small gray text somewhere the
 * actual % calculated ACCURATELY").
 */
export const CombinedRatingCard = styled.section`
  padding: ${spacing[4]} ${spacing[5]};
  background: ${brand.primaryLight};
  border: 1px solid ${border.light};
  border-radius: ${radius.md};

  > h3 {
    margin: 0 0 ${spacing[1]} 0;
    font-size: ${typography.size.xs};
    font-weight: ${typography.weight.semibold};
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${text.muted};
  }
`;

export const CombinedRatingValue = styled.div`
  font-size: ${typography.size.xl};
  font-weight: ${typography.weight.bold};
  color: ${brand.primary};
  line-height: 1.2;
`;

export const CombinedRatingNote = styled.div`
  margin-top: ${spacing[1]};
  font-size: ${typography.size.xs};
  color: ${text.muted};
`;

export const StrategicOverviewCard = styled.section`
  position: relative;
  padding: ${spacing[5]} ${spacing[5]} ${spacing[4]} ${spacing[6]};
  background: ${brand.primaryLight};
  border: 1px solid ${border.light};
  border-left: 3px solid ${brand.primary};
  border-radius: ${radius.md};

  > h3 {
    margin: 0 0 ${spacing[2]} 0;
    font-size: ${typography.size.sm};
    font-weight: ${typography.weight.semibold};
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${brand.primary};
  }
`;

export const StrategicOverviewBody = styled.div`
  font-size: ${typography.size.base};
  line-height: 1.55;
  color: ${text.primary};
`;

export const ConditionAnalysisCollapse = styled.div`
  /*
    Style the antd Collapse to match the AI surface. We can't restyle
    .ant-collapse globally without affecting other usages, so we scope
    via this wrapper class. Tokens are pulled from the same palette as
    the rest of the panel so dark-mode swaps cleanly later.
  */
  .ant-collapse {
    background: ${surface.surface};
    border: 1px solid ${border.light};
    border-radius: ${radius.md};
    overflow: hidden;
  }
  .ant-collapse > .ant-collapse-item {
    border-bottom: 1px solid ${border.light};
  }
  .ant-collapse > .ant-collapse-item:last-child {
    border-bottom: 0;
  }
  .ant-collapse > .ant-collapse-item > .ant-collapse-header {
    align-items: center;
    padding: ${spacing[3]} ${spacing[4]};
    color: ${text.primary};
    font-weight: ${typography.weight.semibold};
    font-size: ${typography.size.base};
  }
  .ant-collapse-content > .ant-collapse-content-box {
    padding: ${spacing[3]} ${spacing[4]} ${spacing[5]};
  }
`;

export const ConditionPanelHeader = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${spacing[3]};
  width: 100%;
  min-width: 0;
`;

export const ConditionPanelTitle = styled.div`
  flex: 1 1 180px;
  min-width: 140px;
  font-size: ${typography.size.base};
  font-weight: ${typography.weight.semibold};
  color: ${text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/**
 * Tone palette for condition metadata chips. Service-connection state is
 * the most important signal so it gets the strong "ready" tone; ratings
 * and diagnostic codes get neutral chips so they don't compete; "not
 * service-connected" gets the muted closed tone so it reads as
 * "documented but not actionable" rather than "failed".
 */
const CHIP_TONES = {
  serviceConnected: status.ready,
  notServiceConnected: status.closed,
  rating: status.processing,
  neutral: status.neutral,
  warning: status.review,
};

export const ConditionMetaChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[1.5]};
  flex: 1 1 260px;
  min-width: 0;
  justify-content: flex-end;
`;

export const ConditionMetaChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px ${spacing[2]};
  border-radius: 999px;
  font-size: ${typography.size.xs};
  font-weight: ${typography.weight.medium};
  line-height: 1.4;
  background: ${({ $tone }) => (CHIP_TONES[$tone] || status.neutral).bg};
  color: ${({ $tone }) => (CHIP_TONES[$tone] || status.neutral).fg};
  border: 1px solid
    ${({ $tone }) => (CHIP_TONES[$tone] || status.neutral).border};
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ConditionAnalysisBody = styled.div`
  font-size: ${typography.size.base};
  line-height: 1.55;
  color: ${text.primary};
`;

export const ConditionFailedBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${spacing[2]};
  padding: ${spacing[2]} ${spacing[3]};
  margin-bottom: ${spacing[3]};
  background: ${status.failed.bg};
  border: 1px solid ${status.failed.border};
  border-radius: ${radius.sm};
  color: ${status.failed.fg};
  font-size: ${typography.size.sm};
  line-height: 1.5;
`;

export const ReportFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${spacing[2]};
  padding-top: ${spacing[3]};
  border-top: 1px solid ${border.light};
`;

export const ReportEmptyBody = styled.div`
  padding: ${spacing[5]};
  text-align: center;
  font-size: ${typography.size.sm};
  color: ${text.muted};
`;

// ---------------------------------------------------------------------------
// Loading skeletons
// ---------------------------------------------------------------------------

const shimmer = keyframes`
  0%   { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

export const BriefingSkeletonBlock = styled.div`
  height: ${({ $h }) => ($h ? `${$h}px` : "12px")};
  width: ${({ $w }) => $w || "100%"};
  border-radius: ${radius.sm};
  background: linear-gradient(
    90deg,
    ${border.light} 0%,
    ${surface.hover} 50%,
    ${border.light} 100%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.4s ease-in-out infinite;
  margin-bottom: ${spacing[2]};
`;

export const BriefingSkeletonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};
`;

// ---------------------------------------------------------------------------
// Phase 5 — Context Analysis tab
// ---------------------------------------------------------------------------

/**
 * Top metadata row for the Context Analysis tab — sits above the
 * markdown body and shows the BE status pill on the left + a
 * "Updated N min ago" timestamp on the right. Mirrors
 * BriefingCategoryRow shape so the Briefing and Context Analysis
 * tabs feel like siblings rather than two different surfaces.
 */
export const ContextAnalysisHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing[2]};
  margin-bottom: ${spacing[3]};
`;

/**
 * Recommended-actions block. Currently always empty in v1 (the BE
 * scaffold returns []), but the renderer is here so the inevitable
 * LLM swap "lights up" the UI without a parallel FE change.
 *
 * Renders as a vertical list of cards — one per action — with an
 * urgency-toned left border so a glance tells the user which
 * actions are time-sensitive.
 */
export const RecommendedActionsSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
  margin-top: ${spacing[4]};
  padding-top: ${spacing[3]};
  border-top: 1px solid ${border.light};
`;

export const RecommendedActionsHeader = styled.h4`
  margin: 0;
  font-size: ${typography.size.md};
  font-weight: ${typography.weight.semibold};
  color: ${text.primary};
`;

const URGENCY_TONES = {
  high: status.failed,
  medium: status.review,
  low: status.processing,
};

export const RecommendedActionItem = styled.article`
  position: relative;
  padding: ${spacing[2.5]} ${spacing[3]} ${spacing[2.5]} ${spacing[4]};
  border-radius: ${radius.md};
  background: ${surface.hover};
  border: 1px solid ${border.light};

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    border-top-left-radius: ${radius.md};
    border-bottom-left-radius: ${radius.md};
    background: ${({ $urgency }) =>
      (URGENCY_TONES[$urgency] || status.neutral).fg};
  }
`;

export const RecommendedActionTitle = styled.div`
  font-size: ${typography.size.md};
  font-weight: ${typography.weight.semibold};
  color: ${text.primary};
  display: flex;
  align-items: center;
  gap: ${spacing[1.5]};
`;

export const RecommendedActionUrgency = styled.span`
  font-size: ${typography.size.xs};
  font-weight: ${typography.weight.semibold};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ $urgency }) => (URGENCY_TONES[$urgency] || status.neutral).fg};
  padding: 2px ${spacing[1.5]};
  border-radius: ${radius.sm};
  background: ${({ $urgency }) =>
    (URGENCY_TONES[$urgency] || status.neutral).bg};
`;

export const RecommendedActionRationale = styled.p`
  margin: ${spacing[1]} 0 0 0;
  font-size: ${typography.size.sm};
  color: ${text.secondary};
  line-height: 1.5;
`;

/**
 * Stale marker — small inline hint that appears above the body when
 * the cached `computed_at` is older than the threshold (default 7d
 * via the consumer). Doesn't block reading; the explicit refresh
 * button (Phase 5b) is the user's escape hatch.
 */
export const ContextAnalysisStaleMarker = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1.5]};
  padding: ${spacing[1]} ${spacing[2.5]};
  border-radius: ${radius.pill};
  background: ${status.review.bg};
  color: ${status.review.fg};
  border: 1px solid ${status.review.border};
  font-size: ${typography.size.sm};
  margin-bottom: ${spacing[2]};
`;

/**
 * Refresh button — sits in the ContextAnalysis header on the right,
 * next to the "Updated …" timestamp. Uses an outlined treatment
 * (instead of a primary button) so it doesn't compete with the
 * action footer's "Mark reviewed" CTA below.
 */
export const ContextAnalysisRefreshButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1.5]};
  padding: ${spacing[1]} ${spacing[2.5]};
  font-size: ${typography.size.sm};
  font-weight: ${typography.weight.medium};
  background: ${surface.surface};
  color: ${brand.primary};
  border: 1px solid ${border.default};
  border-radius: ${radius.sm};
  cursor: pointer;
  transition: background 120ms ease, border-color 120ms ease;

  &:hover:not(:disabled) {
    background: ${brand.primaryLight};
    border-color: ${brand.primary};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

/**
 * Action footer — pinned to the bottom of the AIPane in inbox mode.
 *
 * Layout: a single row of 4 actions (Mark reviewed primary on left,
 * Assign task / Send message / Snooze right). Wraps on narrow widths
 * by collapsing the secondary actions into an overflow menu. We
 * deliberately keep the footer mounted (rather than a sticky overlay)
 * so it doesn't cover the last line of the briefing/context body —
 * AIPane's tab content scrolls inside its own container above this
 * footer.
 */
export const InboxActionFooterContainer = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing[2]};
  padding: ${spacing[3]} ${spacing[4]};
  border-top: 1px solid ${border.light};
  background: ${surface.surface};

  @media (max-width: 1100px) {
    flex-wrap: wrap;
  }

  /*
   * Rail layout (collapsed inbox right rail): the same actions stacked
   * top-to-bottom as icon-only buttons in a narrow column. It takes the
   * full remaining height so the icons sit directly under the expand
   * control rather than pinned to the bottom of an empty strip.
   */
  ${(p) =>
    p.$rail
      ? `
    flex: 1 1 auto;
    min-height: 0;
    flex-direction: column;
    justify-content: flex-start;
    gap: ${spacing[3]};
    padding: ${spacing[3]} 0;
    border-top: none;
    overflow-y: auto;
    overflow-x: hidden;

    @media (max-width: 1100px) {
      flex-wrap: nowrap;
    }
  `
      : ""}
`;

export const InboxActionFooterPrimaryGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};

  ${(p) =>
    p.$rail
      ? `
    flex-direction: column;
    padding-bottom: ${spacing[3]};
    border-bottom: 1px solid ${border.light};
    width: 100%;
  `
      : ""}
`;

export const InboxActionFooterSecondaryGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[1]};

  ${(p) =>
    p.$rail
      ? `
    flex-direction: column;
    gap: ${spacing[2]};
    width: 100%;
  `
      : ""}
`;

/**
 * Plain icon-and-label button for the secondary actions (Assign task,
 * Send message). The Snooze button uses an antd Dropdown so its
 * trigger has its own styling — but we keep the visual rhythm here
 * so the four buttons read as a unit.
 */
export const InboxFooterIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1.5]};
  padding: ${spacing[1.5]} ${spacing[2.5]};
  font-size: ${typography.size.sm};
  font-weight: ${typography.weight.medium};
  color: ${text.secondary};
  background: transparent;
  border: 1px solid transparent;
  border-radius: ${radius.sm};
  cursor: pointer;
  transition: background 120ms ease, color 120ms ease, border-color 120ms ease;

  &:hover:not(:disabled) {
    background: ${surface.hover};
    color: ${text.primary};
    border-color: ${border.light};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* Icon-only square in the collapsed rail; the label moves to a
     tooltip. Fixed size keeps the column of icons aligned. */
  ${(p) =>
    p.$rail
      ? `
    width: 36px;
    height: 36px;
    padding: 0;
    justify-content: center;
    gap: 0;
    flex-shrink: 0;
  `
      : ""}
`;

/**
 * Modal-internal styled bits used by AssignTaskModal / SendMessageModal
 * / SnoozeCustomModal. Kept here (rather than in each modal's own
 * file) so the Phase 5b write surface shares one visual vocabulary
 * with the rest of the AI panel.
 */
export const InboxModalLabel = styled.label`
  display: block;
  font-size: ${typography.size.sm};
  font-weight: ${typography.weight.semibold};
  color: ${text.primary};
  margin-bottom: ${spacing[1]};
`;

export const InboxModalHint = styled.p`
  margin: ${spacing[1]} 0 0 0;
  font-size: ${typography.size.xs};
  color: ${text.muted};
  line-height: 1.5;
`;

export const InboxModalErrorBanner = styled.div`
  margin-top: ${spacing[3]};
  padding: ${spacing[2]} ${spacing[3]};
  font-size: ${typography.size.sm};
  background: ${status.failed.bg};
  color: ${status.failed.fg};
  border: 1px solid ${status.failed.border};
  border-radius: ${radius.sm};
`;

export const InboxModalFieldGroup = styled.div`
  & + & {
    margin-top: ${spacing[3]};
  }
`;

// ---------------------------------------------------------------------------
// Waiting-on banner — the work-items conveyor belt (2026-08-28)
// ---------------------------------------------------------------------------
//
// Rendered above the AI tabs in inbox mode when the open document's
// matter has open work items expecting something inbound. Amber on
// purpose: it's a heads-up ("this paper might be the thing someone
// asked for"), not an error. Each row carries an Attach action that
// volleys the item back to the requester with this document linked.

export const WaitingOnBannerContainer = styled.div`
  flex-shrink: 0;
  padding: ${spacing[2]} ${spacing[4]};
  background: ${status.review.bg};
  border-bottom: 1px solid ${status.review.border};
`;

/* The verdict bar — the return half of the belt. Green where the
   waiting banner is amber: the paper has ARRIVED and wants a verdict,
   as opposed to still being awaited. */
export const VerdictBarContainer = styled.div`
  flex-shrink: 0;
  max-height: 45%;
  overflow-y: auto;
  padding: ${spacing[2]} ${spacing[4]};
  background: ${status.ready.bg};
  border-bottom: 1px solid ${status.ready.border};
`;

export const VerdictBarTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[1.5]};
  font-size: ${typography.size.xs};
  font-weight: ${typography.weight.semibold};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${status.ready.fg};
`;

export const WaitingOnBannerTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[1.5]};
  font-size: ${typography.size.xs};
  font-weight: ${typography.weight.semibold};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${status.review.fg};
`;

export const WaitingOnItemRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing[2]};
  margin-top: ${spacing[1.5]};
`;

export const WaitingOnItemLabel = styled.span`
  font-size: ${typography.size.sm};
  color: ${text.primary};
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const WaitingOnItemMeta = styled.span`
  font-size: ${typography.size.xs};
  color: ${text.muted};
  white-space: nowrap;
`;

/** Red only when the item is genuinely stale/overdue; amber otherwise. */
export const WaitingOnStaleChip = styled.span`
  display: inline-block;
  padding: 0 ${spacing[1.5]};
  margin-right: ${spacing[1]};
  font-size: ${typography.size.xs};
  font-weight: ${typography.weight.semibold};
  border-radius: ${radius.sm};
  background: ${({ $tone }) =>
    $tone === "red" ? status.failed.bg : status.review.bg};
  color: ${({ $tone }) =>
    $tone === "red" ? status.failed.fg : status.review.fg};
  border: 1px solid
    ${({ $tone }) =>
      $tone === "red" ? status.failed.border : status.review.border};
`;

export const WaitingOnAttachButton = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1]};
  padding: ${spacing[1]} ${spacing[2]};
  font-size: ${typography.size.xs};
  font-weight: ${typography.weight.semibold};
  color: ${status.review.fg};
  background: ${surface.surface};
  border: 1px solid ${status.review.border};
  border-radius: ${radius.sm};
  cursor: pointer;
  transition: background 120ms ease;

  &:hover:not(:disabled) {
    background: ${status.review.bg};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// ---------------------------------------------------------------------------
// Phase 5c — Tasks + Messages tabs (right-rail inbox panels)
// ---------------------------------------------------------------------------
//
// Both panels share the same scrollable rail body shape as Briefing /
// Context Analysis: a flex column whose body owns vertical overflow so
// long lists don't push the action footer off screen.
//
// Per-row treatment:
//   - Tasks:    body text on top, status pill row + assignee + relative
//               timestamp underneath, status PATCH controls in the row
//               trailing edge (only visible when the user is the
//               assignee or the assigner).
//   - Messages: body text on top, sender + relative timestamp on the
//               left, "Mark read" trailing affordance on unread
//               messages where the current user is the recipient.

/**
 * Numeric badge that rides next to a tab label (`Tasks (3)`,
 * `Messages (2)`). We don't use antd's `Badge` here because antd's
 * default treatment is a red dot designed for notification chrome,
 * not an inline count chip — too loud against the AI surface palette.
 *
 * The badge is hidden when the count is zero so the tab doesn't
 * read as "empty but somehow noisy."
 */
export const InboxTabBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 6px;
  margin-left: ${spacing[1.5]};
  border-radius: 999px;
  background: ${({ $tone }) =>
    $tone === "unread" ? brand.primary : surface.hover};
  color: ${({ $tone }) =>
    $tone === "unread" ? "#fff" : text.tertiary};
  font-size: ${typography.size.xs};
  font-weight: ${typography.weight.semibold};
  line-height: 1;
  font-variant-numeric: tabular-nums;
`;

/**
 * Shared list container for both Tasks and Messages. Owns the
 * scroll axis so the action footer stays pinned. Padding mirrors
 * AIPaneScrollBody so the panels read as siblings of Briefing.
 */
// See `intentional-scroll` note on AIPaneScrollBody above.
export const InboxListBody = styled.div.attrs({
  className: "intentional-scroll",
})`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: ${spacing[4]} ${spacing[4]} ${spacing[5]};
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};
`;

export const InboxListEmptyState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: ${spacing[8]} ${spacing[6]};
  gap: ${spacing[3]};
  color: ${text.muted};
  font-size: ${typography.size.md};
  line-height: 1.6;
`;

export const InboxListEmptyIconRing = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: ${brand.primaryLight};
  color: ${brand.primary};
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

export const InboxListErrorBanner = styled.div`
  margin: ${spacing[3]} ${spacing[4]} 0;
  padding: ${spacing[2]} ${spacing[3]};
  font-size: ${typography.size.sm};
  background: ${status.failed.bg};
  color: ${status.failed.fg};
  border: 1px solid ${status.failed.border};
  border-radius: ${radius.sm};
`;

/**
 * "With: Bob Smith, Carol Lee" header above a matter-message
 * conversation — makes cc'd/looped-in participants visible without
 * opening a modal, since a thread can now have more than two people.
 */
export const InboxThreadParticipants = styled.div`
  font-size: ${typography.size.xs};
  color: ${text.muted};
  padding: 0 0 ${spacing[2]};
  border-bottom: 1px solid ${border.light};
  margin-bottom: ${spacing[1]};

  strong {
    font-weight: ${typography.weight.semibold};
    color: ${text.secondary};
  }
`;

/**
 * One task / message row. Border + radius + soft hover treatment so
 * the rows feel like cards without the heaviness of a full elevation;
 * unread-message rows get a brand-blue left border to echo the
 * Strategic Overview card pattern in the matter-side analysis report.
 */
export const InboxItemRow = styled.article`
  position: relative;
  padding: ${spacing[3]} ${spacing[3]} ${spacing[2.5]};
  border: 1px solid ${border.light};
  border-radius: ${radius.md};
  background: ${surface.surface};
  transition: border-color 120ms ease, background 120ms ease;

  ${({ $unread }) =>
    $unread
      ? `border-left: 3px solid ${brand.primary}; padding-left: 13px;`
      : ""}

  &:hover {
    border-color: ${border.default};
    background: ${surface.hover};
  }
`;

export const InboxItemBody = styled.div`
  font-size: ${typography.size.md};
  line-height: 1.5;
  color: ${text.primary};
  white-space: pre-wrap;
  word-break: break-word;
`;

export const InboxItemFooterRow = styled.footer`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing[2]};
  margin-top: ${spacing[2]};
  flex-wrap: wrap;
`;

export const InboxItemMeta = styled.span`
  font-size: ${typography.size.xs};
  color: ${text.muted};
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1]};
  flex-wrap: wrap;
`;

export const InboxItemMetaPerson = styled.span`
  font-weight: ${typography.weight.medium};
  color: ${text.secondary};
`;

/**
 * Status pill for tasks. Tone is keyed off the BE status string —
 * open / in_progress are "active" (blue / amber) and completed /
 * cancelled are "closed" (green / muted).
 */
const TASK_STATUS_TONES = {
  open: status.processing,
  in_progress: status.review,
  completed: status.ready,
  cancelled: status.closed,
};

export const TASK_STATUS_LABELS = Object.freeze({
  open: "Open",
  in_progress: "In progress",
  completed: "Completed",
  cancelled: "Cancelled",
});

export const TaskStatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1]};
  padding: 2px ${spacing[1.5]};
  border-radius: 999px;
  font-size: ${typography.size.xs};
  font-weight: ${typography.weight.semibold};
  background: ${({ $status }) =>
    (TASK_STATUS_TONES[$status] || status.neutral).bg};
  color: ${({ $status }) =>
    (TASK_STATUS_TONES[$status] || status.neutral).fg};
  border: 1px solid
    ${({ $status }) => (TASK_STATUS_TONES[$status] || status.neutral).border};
`;

/**
 * Exclamation badge for a task the assigner marked Urgent at creation
 * (Matthew, 2026-08-11). ``status.failed`` rather than a one-off hex:
 * it is the same red this panel already reserves for "needs attention
 * now" (a failed pipeline stage), and Urgent is exactly that signal.
 */
export const UrgentTaskBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 15px;
  height: 15px;
  margin-right: ${spacing[1]};
  border-radius: 50%;
  background: ${status.failed.fg};
  color: #fff;
  font-size: ${typography.size.xs};
  font-weight: ${typography.weight.bold};
  line-height: 1;
  vertical-align: middle;
  flex-shrink: 0;
`;

/**
 * Trailing-edge action group inside a task row. We use antd
 * `Dropdown` for the status PATCH affordance so the menu surface is
 * consistent with the snooze dropdown in the action footer.
 */
export const InboxItemActionGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1]};
`;

export const InboxItemActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1]};
  padding: ${spacing[1]} ${spacing[2]};
  font-size: ${typography.size.xs};
  font-weight: ${typography.weight.medium};
  color: ${text.tertiary};
  background: transparent;
  border: 1px solid ${border.light};
  border-radius: ${radius.sm};
  cursor: pointer;
  transition: background 120ms ease, color 120ms ease, border-color 120ms ease;

  &:hover:not(:disabled) {
    background: ${surface.hover};
    color: ${text.primary};
    border-color: ${border.default};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

/**
 * "Unread" indicator dot on message rows where the current user is
 * the recipient and has not yet marked it read. Lives inside the row
 * footer next to the sender name so the row stays scan-friendly.
 */
export const InboxItemUnreadDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${brand.primary};
  margin-right: ${spacing[1]};
`;

